import { ArrowRightIcon, Button, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../../hooks/useHiddenColumns';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { ColorType } from '../../../../types/global';
import { hexToColorOption } from '../../../../utils/colorUtils';
import { useDeputateLessons } from '../../hooks/useDeputateLessons';

type LessonSubjectsTableType = {
  uuid: string;
  subject: string;
  planned: number | null;
  color: ColorType;
};

type LessonSubjectsTableProps = {
  isEvaluation?: boolean;
  onNavigate: (subjectUuid: string) => void;
};

export const LessonSubjectsTable = observer(({ isEvaluation, onNavigate }: LessonSubjectsTableProps) => {
  const { pimAuthClaims } = useAuthClaims();
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('lesson-subjects-table', {
    color: !isEvaluation,
    sst: !isEvaluation,
    status: !isEvaluation,
  });
  const { sorting, saveSorting } = useColumnsSort('lesson-subjects-table');

  const { subjectData } = useLoadBasicData({ pause: false });

  const { lessons: lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const data: LessonSubjectsTableType[] =
    subjectData?.subjects.map((subject) => {
      const { html, label } = hexToColorOption(subject.timetableConfig?.color ?? '');
      return {
        uuid: subject.uuid,
        subject: subject.name,
        planned:
          lessonsData
            .filter((ld) => ld.subjectUuid === subject.uuid)
            .map((ld) => ld.teachingLoadInfo?.deputat ?? 0)
            .reduce((prev, cur) => prev + cur, 0) ?? 0,
        color: { color: html, colorLabel: label },
      };
    }) ?? [];

  const columns: TableColumns<LessonSubjectsTableType>[] = [
    {
      header: t('lessonUnit.subjectName'),
      id: 'subject',
      accessorKey: 'subject',
      meta: {
        filterName: t('lessonUnit.subjectName'),
      },
      size: 300,
    },
    {
      header: t('deputate.planned'),
      id: 'planned',
      accessorKey: 'planned',
      meta: {
        filterName: t('deputate.planned'),
      },
      accessorFn: (row) => {
        return row.planned?.toFixed(2);
      },
      alignment: 'right',
      size: 50,
    },
    {
      header: t('common.color'),
      accessorKey: 'color',
      id: 'color',
      type: 'color',
    },
  ];

  return (
    <Table<LessonSubjectsTableType>
      showBorderRadius
      showShadow
      showSort
      isOnWhite={false}
      showVisibility
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      sorting={sorting}
      onSortingChange={saveSorting}
      columns={columns}
      data={data}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('subject.title', { count: 2 })} (${t('lesson.title', { count: 2 })}) - ${t(
          'common.schoolYear',
        )} ${schoolYear?.shortName}`,
        filename: `${t('subject.title', { count: 2 })}_${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
      }}
      showActionBar
      actionBarSettings={{
        showExpertFilter: !isEvaluation,
        showPrintButton: true,
      }}
      showRowHover
      onRowClick={(_, row) => onNavigate(row.original.uuid)}
      lastCol={() => {
        return <ArrowRightIcon className='small' />;
      }}
    />
  );
});
